import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import NeonButton from "@/components/NeonButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NeonField = ({ label, hint, children, htmlFor }) => (
  <div className="flex flex-col gap-2">
    <Label
      htmlFor={htmlFor}
      className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#A89CB8]"
    >
      {label}
      {hint && <span className="ml-2 text-[#FF007A]/70">{hint}</span>}
    </Label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/15 px-0 py-2.5 text-base text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF007A] focus:shadow-[0_2px_0_0_rgba(255,0,122,0.7)] transition-all font-body";

const PlanVisitDialog = ({ trigger, open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    num_people: 2,
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      num_people: 2,
      preferred_date: "",
      preferred_time: "",
      message: "",
    });
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.preferred_date || !form.preferred_time) {
      toast.error("Fill the essentials. Name, email, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/visits`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        num_people: Number(form.num_people) || 1,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        message: form.message.trim() || null,
      });
      setDone(true);
      toast.success("You're in. We'll be in touch soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange?.(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        data-testid="plan-visit-dialog"
        className="max-w-xl border border-[#FF007A]/40 bg-[#0A0510]/95 backdrop-blur-2xl text-white p-0 overflow-hidden rounded-none shadow-[0_0_60px_rgba(255,0,122,0.25)]"
      >
        {/* Inner neon frame */}
        <div className="relative">
          <div className="absolute inset-0 pointer-events-none crt-scanlines opacity-25" />
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF007A] to-transparent" />
          <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent" />

          <div className="relative px-7 sm:px-10 py-8 sm:py-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] flicker">
              ◉ Soft booking
            </p>
            <DialogTitle asChild>
              <h3 className="font-display mt-3 text-3xl sm:text-4xl font-black neon-pink leading-[1.05]">
                Plan Your Visit
              </h3>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="mt-3 max-w-md text-sm sm:text-base text-[#C9C0D6] leading-relaxed">
                <span>Lock in your time.</span>
                <br />
                <span>Step into something different.</span>
              </p>
            </DialogDescription>

            {!done ? (
              <form
                onSubmit={handleSubmit}
                className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5"
                data-testid="plan-visit-form"
              >
                <div className="sm:col-span-2">
                  <NeonField label="Your name" htmlFor="pv-name">
                    <input
                      id="pv-name"
                      data-testid="pv-name"
                      className={inputCls}
                      placeholder="Who's stepping in"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      autoComplete="name"
                    />
                  </NeonField>
                </div>

                <NeonField label="Email" htmlFor="pv-email">
                  <input
                    id="pv-email"
                    data-testid="pv-email"
                    type="email"
                    className={inputCls}
                    placeholder="you@somewhere.io"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    autoComplete="email"
                  />
                </NeonField>

                <NeonField label="Phone" hint="optional" htmlFor="pv-phone">
                  <input
                    id="pv-phone"
                    data-testid="pv-phone"
                    type="tel"
                    className={inputCls}
                    placeholder="07..."
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    autoComplete="tel"
                  />
                </NeonField>

                <NeonField label="Number of people" htmlFor="pv-people">
                  <input
                    id="pv-people"
                    data-testid="pv-people"
                    type="number"
                    min={1}
                    max={50}
                    className={inputCls}
                    value={form.num_people}
                    onChange={(e) => update("num_people", e.target.value)}
                  />
                </NeonField>

                <NeonField label="Preferred time" htmlFor="pv-time">
                  <input
                    id="pv-time"
                    data-testid="pv-time"
                    type="time"
                    className={`${inputCls} appearance-none [color-scheme:dark]`}
                    value={form.preferred_time}
                    onChange={(e) => update("preferred_time", e.target.value)}
                  />
                </NeonField>

                <div className="sm:col-span-2">
                  <NeonField label="Preferred date" htmlFor="pv-date">
                    <input
                      id="pv-date"
                      data-testid="pv-date"
                      type="date"
                      className={`${inputCls} appearance-none [color-scheme:dark]`}
                      value={form.preferred_date}
                      onChange={(e) => update("preferred_date", e.target.value)}
                    />
                  </NeonField>
                </div>

                <div className="sm:col-span-2">
                  <NeonField label="Anything we should know" hint="optional" htmlFor="pv-msg">
                    <Textarea
                      id="pv-msg"
                      data-testid="pv-message"
                      rows={3}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 text-white placeholder:text-white/30 focus-visible:ring-0 focus:border-[#FF007A]"
                      placeholder="Birthday, group of friends, that one game we have to find..."
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                    />
                  </NeonField>
                </div>

                <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex flex-col gap-1.5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007A] flicker-slow">
                      ◉ Limited early access slots available
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]/80">
                      Be among the first to experience it
                    </p>
                  </div>
                  <NeonButton
                    type="submit"
                    variant="pink"
                    data-testid="plan-visit-submit"
                    disabled={submitting}
                    className="pulse-glow"
                  >
                    {submitting ? "Sending..." : "Request Visit"}
                  </NeonButton>
                </div>
              </form>
            ) : (
              <div className="mt-10 text-center" data-testid="plan-visit-success">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center border border-[#00F0FF] text-[#00F0FF] flicker-slow">
                  <span className="font-mono text-lg">◉</span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
                  ◉ Signal received
                </p>
                <h4 className="mt-3 font-display text-4xl sm:text-5xl font-black neon-pink leading-none">
                  You&rsquo;re in.
                </h4>
                <p className="mt-5 text-[#C9C0D6] text-base leading-relaxed max-w-sm mx-auto">
                  We&rsquo;ll be in touch soon.
                </p>
                <p className="mt-3 text-white text-base leading-relaxed max-w-sm mx-auto">
                  You&rsquo;re one step closer to stepping inside <span className="neon-cyan">Replay</span>.
                </p>
                <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.32em] text-white/50">
                  Follow along for updates &nbsp;<span className="text-[#FF007A]">▸</span>&nbsp; socials soon
                </p>
                <div className="mt-7">
                  <NeonButton
                    variant="cyan"
                    type="button"
                    onClick={reset}
                    data-testid="plan-visit-another"
                  >
                    Add another time
                  </NeonButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanVisitDialog;
